import { RefObject, useCallback, useEffect, useRef, useState } from "react"
import { useClickAway } from "react-use"

interface UseInlineEditOptions<T> {
  value?: T
  defaultValue?: T
  onSubmit?: (value: T) => void | Promise<void>
  onChange?: (value: T) => void
  validator?: (value: T) => boolean
  formatter?: (value: T) => string
  parser?: (value: string) => T
  ref?: RefObject<HTMLElement | null>
  resetOnInvalid?: boolean
  resetOnEmpty?: boolean
  submitOnChange?: boolean
  disableClickOutside?: boolean
  isEmpty?: (value: T) => boolean
}

export function useInlineEdit<T>({
  value: controlledValue,
  defaultValue,
  onSubmit,
  onChange,
  validator,
  formatter,
  parser,
  ref,
  resetOnInvalid = true,
  resetOnEmpty = true,
  submitOnChange = false,
  disableClickOutside = false,
  isEmpty: customIsEmpty,
}: UseInlineEditOptions<T>) {
  const isControlled = controlledValue !== undefined
  const initialValue = isControlled ? controlledValue : (defaultValue ?? ("" as T))

  const [internalValue, setInternalValue] = useState<T>(initialValue)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const submitQueueRef = useRef(Promise.resolve())

  const value = isControlled ? controlledValue : internalValue
  const stringValue = formatter ? formatter(value) : String(value)

  const isEmpty = customIsEmpty ? customIsEmpty(value) : !value || (typeof value === "string" && value.trim() === "")

  const isValid = validator ? validator(value) : true
  const hasChanged = defaultValue !== undefined && value !== defaultValue

  useEffect(() => {
    if (isControlled && controlledValue !== undefined) {
      setInternalValue(controlledValue)
    }
  }, [controlledValue, isControlled])

  useEffect(() => {
    if (defaultValue !== undefined && !isControlled) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue, isControlled])

  const reset = useCallback(() => {
    if (defaultValue !== undefined) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue])

  const enqueueSubmit = useCallback(
    (nextValue: T) => {
      if (!onSubmit) return Promise.resolve()

      const runSubmit = async () => {
        setIsSubmitting(true)

        try {
          await onSubmit(nextValue)
        } catch {
          reset()
        } finally {
          setIsSubmitting(false)
        }
      }

      submitQueueRef.current = submitQueueRef.current.catch(() => undefined).then(runSubmit)
      return submitQueueRef.current
    },
    [onSubmit, reset]
  )

  const handleValueChange = useCallback(
    (newValue: T | string) => {
      const parsedValue = parser && typeof newValue === "string" ? parser(newValue) : (newValue as T)

      if (!isControlled) {
        setInternalValue(parsedValue)
      }

      onChange?.(parsedValue)

      if (submitOnChange) {
        return enqueueSubmit(parsedValue)
      }
    },
    [enqueueSubmit, isControlled, onChange, parser, submitOnChange]
  )

  // Update the local (uncontrolled) display value without running onChange or submitting.
  // Useful when the caller persists the value through its own path and just needs the
  // input to reflect the change immediately.
  const setValueSilently = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue)
      }
    },
    [isControlled]
  )

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return

    if ((resetOnEmpty && isEmpty) || (resetOnInvalid && !isValid)) {
      reset()
      return
    }

    if (hasChanged && onSubmit) {
      isSubmittingRef.current = true
      setIsSubmitting(true)
      try {
        await onSubmit(value)
      } catch {
        reset()
      } finally {
        ref?.current?.blur()
        isSubmittingRef.current = false
        setIsSubmitting(false)
      }
    }
  }, [value, isEmpty, isValid, hasChanged, onSubmit, reset, resetOnEmpty, resetOnInvalid, ref])

  const handleCancel = useCallback(() => {
    reset()
  }, [reset])

  const handleClear = useCallback(async () => {
    const clearedValue = parser ? parser("") : ("" as T)

    if (!isControlled) {
      setInternalValue(clearedValue)
    }

    onChange?.(clearedValue)
    await enqueueSubmit(clearedValue)
  }, [enqueueSubmit, isControlled, onChange, parser])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault()
        handleSubmit()
      }
      if (event.key === "Escape") {
        event.preventDefault()
        handleCancel()
      }
    },
    [handleSubmit, handleCancel]
  )

  useClickAway(ref || { current: null }, () => {
    if (!disableClickOutside) {
      handleSubmit()
    }
  })

  return {
    value,
    stringValue,
    setValue: handleValueChange,
    setValueSilently,
    isSubmitting,
    isValid,
    isEmpty,
    hasChanged,
    handleSubmit,
    handleCancel,
    handleClear,
    handleKeyDown,
    reset,
  }
}
